"use client";
// libraries/api/src/mutations/health.ts

import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";

import { withManageTripsNextClientHeaders } from "../manage-trips-client-headers";

export type HealthRequest = {
  b6PNRRecordLocator: string;
  lastName: string;
};
type TelemetryHttpError = Error & {
  code?: string;
  errorCode?: string;
  httpStatus?: number;
  status?: number;
  statusCode?: number;
};
// Intentionally local to avoid coupling lower-level API utilities to fe-logger.
function createTelemetryHttpError({
  errorCode,
  message,
  status,
}: {
  errorCode?: string;
  message: string;
  status?: number;
}): TelemetryHttpError {
  return Object.assign(new Error(message), {
    code: errorCode,
    errorCode,
    httpStatus: status,
    status,
    statusCode: status,
  });
}

export type HealthResponse = {
  b6PNRRecordLocator?: string;
  emailExist?: boolean;
  phoneNumberExist?: boolean;
  pnrItinAirSeg?: {
    classOfService: string;
    destination: string;
    destinationCode: string;
    durationInMinutes: number;
    equipmentType: string;
    flightNumber: number;
    operatingAirlineCode: string;
    operatingAirlineShortName: string;
    origin: string;
    originCode: string;
    scheduledArrivalTime: string;
    scheduledDepartureTime: string;
    seatNumber: string;
  }[];
  pnrState: "normal" | "qualifier" | "error" | "disrupted";
  tripImage?: string;
  tripSummary?: {
    destination: string;
    destinationCode: string;
    origin: string;
    originCode: string;
    scheduledDepartureTime: string;
  };
};
export const HEALTH_KEYS = {
  byReq: (request: HealthRequest) =>
    [
      ...HEALTH_KEYS.root(),
      request.b6PNRRecordLocator,
      request.lastName,
    ] as const,
  root: () => ["pnrHealth"] as const,
};

// eslint-disable-next-line no-implicit-coercion -- preferred this pattern
const USE_LOCAL_CRYSTAL_BLUE = !!process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL;

let __healthEnableMockOverride: boolean | undefined;

type ApiMocksGlobal = typeof globalThis & {
  __API_MOCKS__?: boolean;
};

export function __setHealthEnableMockForTests(value?: boolean) {
  __healthEnableMockOverride = value;
}

function isMockEnabled(): boolean {
  if (typeof __healthEnableMockOverride === "boolean") {
    return __healthEnableMockOverride;
  }

  const fromEnvironment =
    (typeof process !== "undefined" &&
      process.env.NEXT_PUBLIC_USE_DEV_MOCKS === "true") ||
    (globalThis as ApiMocksGlobal).__API_MOCKS__ === true;

  return fromEnvironment;
}

// ========== URL resolution + mandatory expand ==========
function resolveHealthUrl(fallbackUrl: string): string {
  const local = process.env.NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL?.trim();
  if (local) {
    return local;
  }

  return fallbackUrl;
}

/**
 * expand is mandatory for this app, and is expected to be baked into the URL.
 * We enforce that the resolved URL includes ?expand=...
 */
function assertExpandInUrl(url: string): string {
  const isRelative = url.startsWith("/");

  const parsedUrl = new URL(
    url,
    // eslint-disable-next-line  @typescript-eslint/no-unnecessary-condition -- prefer this pattern
    globalThis.window == undefined
      ? "http://localhost"
      : globalThis.location.origin,
  );

  // The 'expand' query parameter is required by the service, but we do not
  // enforce it here at runtime because some deployment/local URLs already
  // include the parameter and throwing would break callers; ensure the
  // URL in NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL or MANAGE_TRIPS.API.HEALTH_CHECK_URL
  // contains the required expand value (e.g. ?expand=flightCard).
  // const expand = parsedUrl.searchParams.get("expand");
  // if (!expand?.trim()) {
  //   throw new Error(
  //     "PNR health-check requires `expand` query param. " +
  //       "Include it in NEXT_PUBLIC_LOCAL_PNR_HEALTH_URL (local) or MANAGE_TRIPS.API.HEALTH_CHECK_URL (deployed), " +
  //       "e.g. http://localhost:8080/pnr/health-check?expand=flightCard",
  //   );
  // }

  return isRelative
    ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    : parsedUrl.toString();
}

async function realDoHealth(
  url: string,
  request: HealthRequest,
): Promise<HealthResponse> {
  const response = await fetch(url, {
    body: JSON.stringify(request),
    credentials: USE_LOCAL_CRYSTAL_BLUE ? "same-origin" : "include",
    headers: withManageTripsNextClientHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    method: "POST",
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw createTelemetryHttpError({
      errorCode: `PNR_HEALTH_CHECK_HTTP_${String(response.status)}`,
      message: `Health check failed: ${String(response.status)} ${text}`.trim(),
      status: response.status,
    });
  }

  const text = await response.text().catch(() => "");
  if (!text.trim()) {
    return {
      b6PNRRecordLocator: request.b6PNRRecordLocator,
      emailExist: false,
      phoneNumberExist: false,
      pnrState: "normal",
    };
  }

  return JSON.parse(text) as HealthResponse;
}

// --- Mock POST impl ---
function mockDoHealth(request: HealthRequest): Promise<HealthResponse> {
  const { b6PNRRecordLocator } = request;

  const mock: HealthResponse = {
    b6PNRRecordLocator: b6PNRRecordLocator || "PILYRN",
    emailExist: false,
    phoneNumberExist: false,
    pnrItinAirSeg: [
      {
        classOfService: "Blue Basic",
        destination: "Los Angeles (LAX)",
        destinationCode: "LAX",
        durationInMinutes: 375,
        equipmentType: "E90",
        flightNumber: 123,
        operatingAirlineCode: "B6",
        operatingAirlineShortName: "JETBLUE AIRWAYS",
        origin: "Boston (BOS)",
        originCode: "BOS",
        scheduledArrivalTime: "2024-03-01T17:15:00",
        scheduledDepartureTime: "2024-03-01T11:05:00",
        seatNumber: "23A",
      },
      {
        classOfService: "Blue Basic",
        destination: "Toronto (YYZ)",
        destinationCode: "YYZ",
        durationInMinutes: 360,
        equipmentType: "E90",
        flightNumber: 467,
        operatingAirlineCode: "B6",
        operatingAirlineShortName: "JETBLUE AIRWAYS",
        origin: "Los Angeles (LAX)",
        originCode: "LAX",
        scheduledArrivalTime: "2024-03-01T23:30:00",
        scheduledDepartureTime: "2024-03-01T17:30:00",
        seatNumber: "46C",
      },
      {
        classOfService: "Blue Basic",
        destination: "Boston (BOS)",
        destinationCode: "BOS",
        durationInMinutes: 85,
        equipmentType: "E90",
        flightNumber: 357,
        operatingAirlineCode: "B6",
        operatingAirlineShortName: "JETBLUE AIRWAYS",
        origin: "Toronto (YYZ)",
        originCode: "YYZ",
        scheduledArrivalTime: "2024-03-03T11:30:00",
        scheduledDepartureTime: "2024-03-03T10:05:00",
        seatNumber: "22C",
      },
    ],
    pnrState: "disrupted",
    tripImage:
      "https://www-dev2.jetblue.com/magnoliapublic/dam/flightBlockImagePrefix/YYZ.jpg",
  };

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(mock);
    }, 300);
  });
}

export function doHealth(_request: HealthRequest): Promise<HealthResponse> {
  throw new Error(
    "Use useHealthMutation() to call doHealth with configured URL.",
  );
}
export async function callHealth(options: {
  request: HealthRequest;
  url: string;
}): Promise<HealthResponse> {
  const { request, url: fallbackUrl } = options;

  if (isMockEnabled()) {
    return mockDoHealth(request);
  }

  const url = resolveHealthUrl(fallbackUrl);

  if (!url) {
    throw new Error(
      "[callHealth] Missing MANAGE_TRIPS.API.HEALTH_CHECK_URL runtime config",
    );
  }

  const finalUrl = assertExpandInUrl(url);
  return realDoHealth(finalUrl, request);
}
export const healthOptions = (request: HealthRequest) =>
  queryOptions({
    queryFn: () => {
      throw new Error(
        "healthOptions has no GET queryFn; use mutation to seed cache.",
      );
    },
    queryKey: HEALTH_KEYS.byReq(request),
    staleTime: 60_000,
  });
export function useHealthMutation() {
  const {
    MANAGE_TRIPS: {
      API: { HEALTH_CHECK_URL },
    },
  } = useManageTripsConfig();

  const queryClient = useQueryClient();

  return useMutation({
    gcTime: 10 * 60_000,
    mutationFn: async (variables: HealthRequest) => {
      if (isMockEnabled()) {
        return mockDoHealth(variables);
      }

      const url = resolveHealthUrl(HEALTH_CHECK_URL);

      if (!url) {
        throw new Error(
          "Missing MANAGE_TRIPS.API.HEALTH_CHECK_URL runtime config",
        );
      }

      const finalUrl = assertExpandInUrl(url);
      return realDoHealth(finalUrl, variables);
    },
    networkMode: "always",
    onSuccess: (data, variables) => {
      queryClient.setQueryData(HEALTH_KEYS.byReq(variables), data);
    },
    retry: 1,
  });
}
